import React from 'react';
import { Star, Zap, Users } from 'lucide-react';
import UltimatePromptBundleCard from '@/components/cards/homepage/UltimatePromptBundleCard';
import { useProductPricing } from '@/hooks/useFeaturedPricing';

// Product slug for the Ultimate Prompt Bundle
const ULTIMATE_PROMPT_BUNDLE_SLUG = 'ultimate-ai-prompt-bundle';

/**
 * UltimatePromptBundleSection Component
 *
 * Displays a compelling section for the Ultimate AI Prompt & Guide Bundle
 * digital product. Uses warm amber/orange theme to differentiate from
 * course bundles.
 *
 * @component
 */
export default function UltimatePromptBundleSection() {
  // Fetch pricing for the digital product
  const { pricing, loading } = useProductPricing(ULTIMATE_PROMPT_BUNDLE_SLUG, 'digital_product');

  return (
    <section className="py-24 px-6 bg-gradient-to-br from-amber-900/10 via-black to-orange-900/10 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(245, 158, 11, 0.15) 0%, transparent 60%)',
      }}></div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-6 py-2 bg-gradient-to-r from-amber-600/20 via-orange-600/20 to-red-600/20 border border-amber-500/30 rounded-full text-sm mb-6">
            <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent font-semibold flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              NEW: INSTANT DOWNLOAD BUNDLE
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="block">Skip the Learning Curve</span>
            <span className="block bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
              Get Results Today
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Why spend hours crafting prompts when you can have <span className="text-amber-400 font-semibold">1,400+ proven templates</span> ready to copy and paste? Get instant results with ChatGPT, Claude, and any AI tool.
          </p>
        </div>

        {/* Bundle Card */}
        <UltimatePromptBundleCard pricing={pricing} loading={loading} />

        {/* Social Proof */}
        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-amber-500/20 px-6 py-4 rounded-full">
            <div className="flex -space-x-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 border-2 border-black"></div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-red-400 border-2 border-black"></div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-400 to-pink-400 border-2 border-black"></div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-amber-400 border-2 border-black"></div>
            </div>
            <div className="text-left">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-sm text-gray-400">
                <span className="text-white font-semibold">Thousands of professionals</span> saving hours daily
              </p>
            </div>
          </div>
        </div>

        {/* Use Cases Row */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-full text-sm text-gray-400">
            <Users className="w-4 h-4 inline mr-2 text-amber-400" />
            Entrepreneurs
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-full text-sm text-gray-400">
            <Users className="w-4 h-4 inline mr-2 text-orange-400" />
            Marketers
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-full text-sm text-gray-400">
            <Users className="w-4 h-4 inline mr-2 text-red-400" />
            Developers
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-full text-sm text-gray-400">
            <Users className="w-4 h-4 inline mr-2 text-yellow-400" />
            Content Creators
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-full text-sm text-gray-400">
            <Users className="w-4 h-4 inline mr-2 text-amber-400" />
            Freelancers
          </div>
        </div>
      </div>
    </section>
  );
}
